import { useState } from 'react';
import { Select, Group, Button, Table, Loader, Center, Text, Modal, Stack, NumberInput, Badge, SegmentedControl, ActionIcon, Tooltip } from '@mantine/core';
import { DatePickerInput, DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconChecklist, IconPencil, IconTrash } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TablePagination } from '../components/TablePagination';
import { useClasses, useSubjects } from '../hooks/useLookups';
import { useAcademicTerms, useCurrentTerm } from '../hooks/useAcademicTerms';
import { FetchOverlay } from '../components/FetchOverlay';
import { ServerSearchSelect } from '../components/ServerSearchSelect';
import { TableToolbar } from '../components/TableToolbar';
import { useDebouncedValue } from '@mantine/hooks';
import { PageHeader } from '../components/PageHeader';
import { CreatableSelect, QuickSubjectForm } from '../components/QuickCreate';
import { SectionCard } from '../components/SectionCard';
import { GradeEditModal, type EditableGrade } from '../components/GradeEditModal';
import { formatDate } from '../lib/format';
import { confirmDelete } from '../lib/confirm';
import { useAuth } from '../contexts/AuthContext';
import { isoDate, gradeColor, termForDate, apiError } from '../lib/journal';

interface StudentOpt { id: string; fullName: string }
/** GET /grades/teachers — FAQAT baho qo'ygan foydalanuvchilar (T5 filtri uchun) */
interface GradeTeacherOpt { id: string; fullName: string; gradeCount: number }
interface Grade {
  id: string;
  value: number;
  term?: string;
  /** Elektron jurnal: chorakka bog'lanish. Eski yozuvlarda `null` (legacy). */
  termId?: string | null;
  comment?: string | null;
  subjectId?: string;
  date: string;
  student?: { fullName: string };
  subject?: { name: string };
  /** T5: bahoni KIM qo'ygani (backend `include.teacher`) */
  teacher?: { id: string; fullName: string } | null;
}

/**
 * ZAXIRA chorak ro'yxati — FAQAT `/academic-terms` bo'sh yoki mavjud emas bo'lganda.
 * Bahoning chorak maydoni ilgari ERKIN MATN edi ("1-chorak" / "1 chorak" / "I chorak"),
 * filtr esa Select — mos kelmagan baho hisobotdan jimgina tushib qolardi. Endi ikkala
 * tomon ham BITTA ro'yxatdan quriladi; choraklar sozlanmagan bo'lsa ham foydalanuvchi
 * qo'lda matn yoza olmaydi.
 */
const LEGACY_TERMS = [1, 2, 3, 4];

/** O'qituvchi FAQAT o'zi qo'ygan bahoni tuzata/o'chira oladi (backend `grades.ts` bilan bir xil). */
const TEACHER_ROLES = ['TEACHER', 'HOMEROOM_TEACHER'];

export default function Grades() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [classId, setClassId] = useState<string | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<EditableGrade | null>(null);

  const { data: classes } = useClasses();
  const { data: subjects } = useSubjects();
  // Choraklar — filtr va baho qo'yish formasi UCHUN YAGONA manba (kontrakt 2.1)
  const { data: terms, isError: termsError } = useAcademicTerms();
  const { data: currentTerm } = useCurrentTerm();
  const hasTerms = !!terms?.length;
  const { data: students } = useQuery({
    queryKey: ['students-of-class', classId],
    queryFn: async () => (await api.get<StudentOpt[]>('/students', { params: { classId } })).data,
    enabled: !!classId,
  });
  /**
   * T5: "kim qo'ygan" filtri uchun ro'yxat. `/staff` ATAYLAB ishlatilmaydi — u faqat
   * SUPER_ADMIN/DIRECTOR/ADMIN uchun ochiq, baholar sahifasi esa o'qituvchiga ham
   * ko'rinadi va select 403 bilan bo'sh qolardi.
   */
  const { data: gradeTeachers } = useQuery({
    queryKey: ['grade-teachers'],
    queryFn: async () => (await api.get<GradeTeacherOpt[]>('/grades/teachers')).data,
  });
  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [subjF, setSubjF] = useState<string>('');
  const [studentF, setStudentF] = useState<string>('');
  const [teacherF, setTeacherF] = useState<string>('');
  const [termF, setTermF] = useState<string>('');
  // Davr filtri: '' — barcha davr, aks holda backend Toshkent kalendari bo'yicha kesadi
  const [periodF, setPeriodF] = useState<string>('');
  // Aniq sana oralig'i (from/to) backendda `period`dan USTUN — shuning uchun ikkalasi
  // bir vaqtda faol bo'lmaydi: biri tanlansa ikkinchisi tozalanadi (chalkashmaslik uchun).
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [sort, setSort] = useState('date:desc');
  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    classId: classId || undefined, q: qDeb || undefined, subjectId: subjF || undefined,
    studentId: studentF || undefined, teacherId: teacherF || undefined, term: termF || undefined,
    period: periodF || undefined, from: isoDate(range[0]), to: isoDate(range[1]), sort,
  };

  const { items: grades, isLoading, fetching, pages, page, setPage, total, limit, setLimit} = usePagedQuery<Grade>('grades', '/grades', filtrlar);
  const anyFilter = !!(qDeb || subjF || studentF || teacherF || termF || periodF || range[0] || range[1]);

  /**
   * Chorak FILTRI — qiymat `Grade.term` MATNI (backend `?term=` shu bo'yicha filtrlaydi).
   * Choraklar jadvali sozlangan bo'lsa nomlar o'sha yerdan, aks holda eski qat'iy ro'yxat.
   */
  const termFilterOptions = hasTerms
    ? terms.map((tm) => ({ value: tm.name, label: tm.name }))
    : LEGACY_TERMS.map((n) => ({ value: `${n}-chorak`, label: t('gradesPage.termN', { n }) }));

  /** Sana oralig'i tanlansa — `period` tushiriladi (backend from/to ni ustun ko'radi). */
  const onRangeChange = (v: [Date | null, Date | null]) => {
    setRange(v);
    if (v[0] || v[1]) setPeriodF('');
    setPage(1);
  };
  /** Davr tanlansa — teskarisi: aniq oraliq tozalanadi, aks holda u e'tiborga olinmasdi. */
  const onPeriodChange = (v: string) => {
    setPeriodF(v);
    if (v) setRange([null, null]);
    setPage(1);
  };

  /**
   * Baho qo'yish formasi. `term` maydoni endi TANLOV:
   *  • choraklar sozlangan bo'lsa qiymat — chorak ID'si (`termId` bo'lib ketadi);
   *  • sozlanmagan bo'lsa — eski matn ("1-chorak"), `term` bo'lib ketadi.
   */
  const form = useForm({
    initialValues: { studentId: '', subjectId: '', value: 5, term: '', date: new Date() as Date | null },
    validate: {
      studentId: (v) => (!v ? t('gradesPage.selectStudent') : null),
      subjectId: (v) => (!v ? t('gradesPage.selectSubject') : null),
      value: (v) => (v < 1 || v > 5 ? t('gradesPage.gradeRange') : null),
    },
  });

  /** Modal ochilganda: chorakni bugungi sana bo'yicha oldindan tanlaymiz. */
  const openCreate = () => {
    const preset = hasTerms
      ? (termForDate(terms, new Date())?.id ?? currentTerm?.id ?? terms[0].id)
      : '1-chorak';
    form.setValues({ studentId: '', subjectId: '', value: 5, term: preset, date: new Date() });
    form.clearErrors();
    open();
  };

  /** Sana o'zgarsa — chorak ham o'sha sanaga mos kelganiga ko'chadi (mos kelmasa tegilmaydi). */
  const onFormDateChange = (d: Date | null) => {
    form.setFieldValue('date', d);
    if (!hasTerms) return;
    const match = termForDate(terms, d);
    if (match) form.setFieldValue('term', match.id);
  };

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) => {
      const picked = hasTerms ? terms.find((tm) => tm.id === v.term) : undefined;
      return api.post('/grades', {
        studentId: v.studentId,
        subjectId: v.subjectId,
        value: v.value,
        date: isoDate(v.date),
        // `termId` — yangi kontrakt (2.2); `term` matni esa LEGACY filtr/hisobot uchun
        // parallel yoziladi, shunda ikkala tomon ham bir xil nomni ko'radi.
        ...(picked ? { termId: picked.id, term: picked.name } : v.term ? { term: v.term } : {}),
      });
    },
    onSuccess: () => { notifications.show({ color: 'green', message: t('gradesPage.gradeAdded') }); qc.invalidateQueries({ queryKey: ['grades'] }); form.reset(); close(); },
  });

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/grades/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('gradesPage.deleted') }); qc.invalidateQueries({ queryKey: ['grades'] }); },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  const isTeacher = TEACHER_ROLES.includes(user?.role ?? '');
  /** O'qituvchi begona bahoga tegmaydi — tugmalar ham ko'rsatilmaydi (403 kutilmasin). */
  const canEditGrade = (g: Grade) => !isTeacher || g.teacher?.id === user?.id;

  return (
    <>
      <PageHeader
        title={t('pages.grades.title')}
        action={<Button leftSection={<IconPlus size={18} />} disabled={!classId} onClick={openCreate}>{t('gradesPage.addGrade')}</Button>}
      />

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('gradesPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'date:desc', label: t('att.sortDateDesc') },
          { value: 'value:desc', label: t('gradesPage.sortValueDesc') },
          { value: 'value:asc', label: t('gradesPage.sortValueAsc') },
        ]}
      >
        <Select
          placeholder={t('fields.class')}
          data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={classId}
          // Sinf almashsa — o'quvchi filtri boshqa sinfniki bo'lib qolmasin
          onChange={(v) => { setClassId(v); setStudentF(''); setPage(1); }}
          clearable searchable w={150}
        />
        <Select
          placeholder={t('fields.subject')}
          data={(subjects ?? []).map((sub) => ({ value: sub.id, label: sub.name }))}
          value={subjF || null}
          onChange={(v) => { setSubjF(v ?? ''); setPage(1); }}
          clearable searchable w={160}
        />
        <ServerSearchSelect
          placeholder={t('gradesPage.student')}
          endpoint="/students"
          params={{ classId: classId || undefined }}
          mapOption={(st: { id: string; fullName: string }) => ({ value: st.id, label: st.fullName })}
          value={studentF || null}
          onChange={(v) => { setStudentF(v ?? ''); setPage(1); }}
          clearable w={190}
        />
        <Select
          placeholder={t('gradesPage.teacher')}
          // Yorliqda baho SONI ham ko'rsatiladi — ro'yxatdan kimni tanlash
          // kerakligi (kim faol baho qo'yayotgani) darrov ko'rinadi.
          data={(gradeTeachers ?? []).map((tt) => ({ value: tt.id, label: `${tt.fullName} (${tt.gradeCount})` }))}
          value={teacherF || null}
          onChange={(v) => { setTeacherF(v ?? ''); setPage(1); }}
          clearable searchable w={190}
          nothingFoundMessage={t('common.noResults')}
        />
        <Select
          placeholder={t('gradesPage.term')}
          data={termFilterOptions}
          value={termF || null}
          onChange={(v) => { setTermF(v ?? ''); setPage(1); }}
          clearable w={150}
          description={termsError ? t('gradesPage.termsError') : undefined}
        />
        <SegmentedControl
          size="sm"
          value={periodF}
          onChange={onPeriodChange}
          data={[
            { value: '', label: t('gradesPage.periodAll') },
            { value: 'day', label: t('gradesPage.periodDay') },
            { value: 'week', label: t('gradesPage.periodWeek') },
            { value: 'month', label: t('gradesPage.periodMonth') },
          ]}
        />
        <DatePickerInput
          type="range"
          placeholder={t('att.dateRange')}
          value={range}
          onChange={onRangeChange}
          valueFormat="DD.MM.YYYY"
          clearable w={210}
        />
        <TableExport<Grade>
          endpoint="/grades"
          params={filtrlar}
          filename={t('nav.grades')}
          ustunlar={[
            { key: 'student', label: t('att.student'), get: (r) => r.student?.fullName ?? '' },
            { key: 'subject', label: t('fields.subject'), get: (r) => r.subject?.name ?? '' },
            { key: 'value', label: t('gradesPage.grade') },
            { key: 'term', label: t('gradesPage.term') },
            { key: 'date', label: t('att.date') },
          ]}
          sanaMaydoni={(r) => r.date}
        />
      </TableToolbar>

      {/* Karta doim ko'rinadi — ichida tanlov/yuklanish/bo'sh holat/jadval almashadi */}
      <SectionCard title={t('common.list')} icon={IconChecklist} p={0} pos="relative">
        <FetchOverlay visible={fetching} />
        {!classId ? (
          <Center h={140}><Text c="dimmed">{t('gradesPage.selectClassToView')}</Text></Center>
        ) : isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !grades.length ? (
          <Center h={140}><Text c="dimmed">{anyFilter ? t('common.noMatch') : t('gradesPage.noGrades')}</Text></Center>
        ) : (
          /* "O'qituvchi" va amallar ustunlari qo'shilgani uchun minWidth kengaytirildi */
          <Table.ScrollContainer minWidth={820}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('att.student')}</Table.Th>
                  <Table.Th>{t('fields.subject')}</Table.Th>
                  <Table.Th>{t('gradesPage.grade')}</Table.Th>
                  <Table.Th>{t('gradesPage.teacher')}</Table.Th>
                  <Table.Th>{t('gradesPage.term')}</Table.Th>
                  <Table.Th>{t('att.date')}</Table.Th>
                  <Table.Th w={90}>{t('gradesPage.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {grades.map((g) => (
                  <Table.Tr key={g.id}>
                    <Table.Td fw={500}>{g.student?.fullName ?? '—'}</Table.Td>
                    <Table.Td>{g.subject?.name ?? '—'}</Table.Td>
                    <Table.Td>
                      <Badge size="lg" color={gradeColor(g.value)}>{g.value}</Badge>
                    </Table.Td>
                    <Table.Td>{g.teacher?.fullName ?? '—'}</Table.Td>
                    <Table.Td>{g.term ?? <Text c="dimmed" fz="sm" lh={1.4}>{t('gradesPage.noTermSet')}</Text>}</Table.Td>
                    <Table.Td>{formatDate(g.date)}</Table.Td>
                    <Table.Td>
                      {canEditGrade(g) ? (
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label={t('gradesPage.edit')}>
                            <ActionIcon
                              variant="subtle"
                              aria-label={t('gradesPage.edit')}
                              onClick={() => setEditing({ id: g.id, value: g.value, comment: g.comment, date: g.date, subjectId: g.subjectId ?? null })}
                            >
                              <IconPencil size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('gradesPage.deleteGrade')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={t('gradesPage.deleteGrade')}
                              loading={delMut.isPending && delMut.variables === g.id}
                              onClick={() => confirmDelete(
                                `${g.student?.fullName ?? ''} · ${g.subject?.name ?? ''} · ${g.value}`,
                                () => delMut.mutate(g.id),
                              )}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      ) : (
                        <Tooltip label={t('gradesPage.onlyOwnGrade')}>
                          <Text c="dimmed" fz="sm" lh={1.4}>—</Text>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      <Modal opened={opened} onClose={close} title={t('gradesPage.addGrade')} centered>
        <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            <Select label={t('att.student')} data={(students ?? []).map((s) => ({ value: s.id, label: s.fullName }))} searchable required {...form.getInputProps('studentId')} />
            <CreatableSelect
              label={t('fields.subject')}
              required
              data={(subjects ?? []).map((s) => ({ value: s.id, label: s.name }))}
              value={form.values.subjectId}
              onChange={(v) => form.setFieldValue('subjectId', v || '')}
              error={form.errors.subjectId}
              createTitle={t('pages.subjects.add')}
              renderCreate={(onCreated) => <QuickSubjectForm onCreated={onCreated} />}
            />
            <NumberInput label={t('gradesPage.gradeValue')} min={1} max={5} required {...form.getInputProps('value')} />
            <DateInput
              label={t('gradesPage.date')}
              valueFormat="DD.MM.YYYY"
              value={form.values.date}
              onChange={onFormDateChange}
            />
            {/* ERKIN MATN O'RNIGA TANLOV — nomlar `/academic-terms` dan (kontrakt 3.5) */}
            <Select
              label={t('gradesPage.term')}
              placeholder={t('gradesPage.selectTerm')}
              data={hasTerms
                ? terms.map((tm) => ({ value: tm.id, label: tm.name }))
                : LEGACY_TERMS.map((n) => ({ value: `${n}-chorak`, label: t('gradesPage.termN', { n }) }))}
              value={form.values.term || null}
              onChange={(v) => form.setFieldValue('term', v ?? '')}
              // Majburiy tanlov: `clearable` bermaslikning O'ZI yetarli emas —
              // `allowDeselect` busiz tanlangan variantni qayta bosish bilan bo'shatilardi.
              allowDeselect={false}
              description={hasTerms ? undefined : t('gradesPage.termsHint')}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={createMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <GradeEditModal
        grade={editing}
        subjects={subjects}
        onClose={() => setEditing(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['grades'] })}
      />
    </>
  );
}
